'use client'

import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getPartners } from '@/services/shared/partnersRepository'
import { useClientIdentityVersion } from '@/hooks/useClientIdentityVersion'
import { withAnalyticsTracking } from '@/utilities/analytics/apiAnalytics'
import {
  mapPartnerToCachedPartner,
  setPartnersCache,
  type CachedPartner,
} from '@/utilities/partnersCache'

export const CLIENT_PARTNERS_QUERY_KEY = ['client-partners'] as const

async function fetchClientPartners(): Promise<CachedPartner[]> {
  const partners = await withAnalyticsTracking(
    async () => {
      const { data, error } = await getPartners()
      if (error) throw error
      if (!data) throw new Error('Partner data was unavailable')
      return data.map(mapPartnerToCachedPartner)
    },
    {
      eventName: 'client:partner_list:partners_api',
      additionalProperties: { source: 'client_portal_context' },
      getSuccessProperties: (partners) => ({ partnerCount: partners.length }),
    },
  )
  setPartnersCache(partners)
  return partners
}

export function useClientPartners({ enabled = true }: { enabled?: boolean } = {}): {
  partners: CachedPartner[]
  fetchSettled: boolean
  loadFailed: boolean
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
} {
  const identityVersion = useClientIdentityVersion()
  const query = useQuery({
    queryKey: [...CLIENT_PARTNERS_QUERY_KEY, identityVersion] as const,
    enabled,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: fetchClientPartners,
  })
  const partners = useMemo(() => query.data ?? [], [query.data])
  const queryRefetch = query.refetch
  const refetch = useCallback(async () => {
    await queryRefetch()
  }, [queryRefetch])

  return {
    partners,
    fetchSettled: enabled && (query.isSuccess || query.isError),
    loadFailed: enabled && query.isError,
    loading: enabled && query.isPending,
    error: query.error instanceof Error ? query.error.message : null,
    refetch,
  }
}
